import type { ListboxFieldOption } from "@/components/flowbite/forms/fields/listbox/types";
import type { RadioOption } from "@/components/flowbite/forms/fields/radio/types";

import {
  PlottingColorByEnum,
  PlottingEthnicityEnum,
  PlottingProfileEnum,
  PlottingSchoolTypeEnum,
  PlottingSpecialMarkersEnum,
} from "../_enums/plotting-filters.enum";
import { getGradeSortOrder } from "../_utils/grade-display.utils";

import type { GradeColorMap } from "../../../../../providers/maps/contexts/plotting-filters-context.types";
import type { PlottingFiltersFormValues } from "../_schemas/plotting-filters.schema";

export const PLOTTING_FILTER_NONE_VALUE = "none";
export const DEFAULT_PLOTTING_COLOR_BY = PlottingColorByEnum.SCHOOL;

export function resolvePlottingColorByDefault(
  colorBy: PlottingColorByEnum | null | undefined,
): PlottingColorByEnum {
  return colorBy ?? DEFAULT_PLOTTING_COLOR_BY;
}

export const DEFAULT_FILTERS: PlottingFiltersFormValues = {
  importPeriod: PLOTTING_FILTER_NONE_VALUE,
  colorBy: DEFAULT_PLOTTING_COLOR_BY,
  schoolType: PlottingSchoolTypeEnum.ALL,
  attendingSchoolIds: [],
  fromGrade: PLOTTING_FILTER_NONE_VALUE,
  toGrade: PLOTTING_FILTER_NONE_VALUE,
};

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export const plottingColorByNames: Record<PlottingColorByEnum, string> = {
  [PlottingColorByEnum.NONE]: "None",
  [PlottingColorByEnum.SCHOOL]: "School",
  [PlottingColorByEnum.GRADE]: "Grade",
} as const;

export const colorByOptions: ListboxFieldOption[] = Object.entries(plottingColorByNames).map(
  ([value, label]) => ({ value, label }),
);

export const plottingSchoolTypeNames: Record<PlottingSchoolTypeEnum, string> = {
  [PlottingSchoolTypeEnum.ALL]: "All Schools",
  [PlottingSchoolTypeEnum.K8]: "K-8",
  [PlottingSchoolTypeEnum.ELEMENTARY]: "Elementary",
  [PlottingSchoolTypeEnum.MIDDLE]: "Middle",
  [PlottingSchoolTypeEnum.HIGH]: "High",
  [PlottingSchoolTypeEnum.INDIVIDUAL]: "Individual Schools",
} as const;

export const schoolTypeOptions: ListboxFieldOption[] = Object.entries(plottingSchoolTypeNames).map(
  ([value, label]) => ({ value, label }),
);

export const plottingProfileNames: Record<PlottingProfileEnum, string> = {
  [PlottingProfileEnum.STUDENT]: "Student",
  [PlottingProfileEnum.SCHOOL]: "School",
  [PlottingProfileEnum.DISTRICT]: "District",
} as const;

export const profileOptions: RadioOption[] = Object.entries(plottingProfileNames).map(
  ([value, label]) => ({ value, label }),
);

export const plottingEthnicityNames: Record<PlottingEthnicityEnum, string> = {
  [PlottingEthnicityEnum.ASIAN]: "Asian",
  [PlottingEthnicityEnum.HISPANIC]: "Hispanic",
  [PlottingEthnicityEnum.WHITE]: "White",
  [PlottingEthnicityEnum.BLACK]: "Black",
  [PlottingEthnicityEnum.OTHER]: "Other",
} as const;

export const ethnicityOptions: ListboxFieldOption[] = Object.entries(plottingEthnicityNames).map(
  ([value, label]) => ({ value, label }),
);

export const plottingSpecialMarkersNames: Record<PlottingSpecialMarkersEnum, string> = {
  [PlottingSpecialMarkersEnum.SDC_MILD]: "SDC Mild",
  [PlottingSpecialMarkersEnum.SDC_MODERATE]: "SDC Moderate",
  [PlottingSpecialMarkersEnum.SDC_SEVERE]: "SDC Severe",
  [PlottingSpecialMarkersEnum.GIFTED]: "Gifted",
  [PlottingSpecialMarkersEnum.OTHER]: "Other",
} as const;

export const specialMarkersOptions: ListboxFieldOption[] = Object.entries(plottingSpecialMarkersNames).map(
  ([value, label]) => ({ value, label }),
);

/**
 * Color palette for grades
 * Used for assigning colors to students based on their grade
 * Supports grades: 18-22 (PreK 1-3, TK, K) and 1-12 (regular grades)
 */
export const GRADE_COLOR_PALETTE = [
  "#FF6B6B", // PreK 1 (18) - Red
  "#FF8E53", // PreK 2 (19) - Orange-Red
  "#FFA726", // PreK 3 (20) - Orange
  //--------------------------------
  "#5a4a86", // TK (21) - Purple
  "#445f9a", // K (22) - Blue-Purple
  "#3b77a1", // Grade 1 - Blue
  "#3c8a8d", // Grade 2 - Teal-Blue
  "#4a9c78", // Grade 3 - Teal-Green
  "#6eaf6b", // Grade 4 - Green
  "#9dbf62", // Grade 5 - Light Green
  "#d0c95c", // Grade 6 - Yellow-Green
  "#eab845", // Grade 7 - Yellow
  "#e58f2a", // Grade 8 - Orange
  "#d96b1b", // Grade 9 - Red-Orange
  "#c24a19", // Grade 10 - Red
  "#9c2b1a", // Grade 11 - Dark Red
  "#6a0b1e", // Grade 12 - Very Dark Red
] as const;

/**
 * Maps grade number to color from palette
 * @param grade - Grade number or string (18-22 for early childhood, 1-12 for regular grades). API may return string.
 * @returns Color hex string
 */
export function getGradeColor(grade: number | string): string {
  const order = getGradeSortOrder(grade);
  if (order === 999) return GRADE_COLOR_PALETTE[0];
  const i = Math.min(Math.max(0, order + 4), GRADE_COLOR_PALETTE.length - 1);

  return GRADE_COLOR_PALETTE[i];
}

/**
 * Creates a map of all grades to their colors
 * @returns Map of grade number to color hex string
 */
export function createGradeColorMap(): GradeColorMap {
  const map = new Map<number, string>();
  for (let grade = -4; grade <= 0; grade++) {
    map.set(grade, getGradeColor(grade));
  }

  for (let grade = 18; grade <= 22; grade++) {
    map.set(grade, getGradeColor(grade));
  }

  for (let grade = 1; grade <= 13; grade++) {
    map.set(grade, getGradeColor(grade));
  }
  return map;
}
